import { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../stores/authStore';
import { usePermissions, Permissions } from '../features/admin/hooks/usePermissions';
import { useTenantBranding } from '../hooks/useTenantBranding';
import { ResponsiveNavDrawer } from '../components/navigation/ResponsiveNavDrawer';
import { MobileBottomNav } from '../components/navigation/MobileBottomNav';
import { LanguageSelector } from '../components/navigation/LanguageSelector';
import { useLanguage } from '../i18n/LanguageContext';

export function AppLayout() {
  const { user, logout } = useAuth();
  const { isTenantAdmin, isPlatformAdmin, can } = usePermissions();
  const { branding } = useTenantBranding();
  const { t } = useLanguage();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  const isAdminSection = location.pathname.startsWith('/admin') && !location.pathname.startsWith('/admin/reports');
  const isReportsSection = location.pathname.startsWith('/admin/reports') || location.pathname.startsWith('/reports');
  const isScenarioSection = location.pathname.startsWith('/scenarios');
  const isAttemptsSection = location.pathname.startsWith('/attempts');
  const isDashboardSection = location.pathname === '/dashboard' || location.pathname === '/';

  const topNavLogo = branding?.topNavLogoUrl;
  const orgName = branding?.name;

  return (
    <div className="app-layout" style={{ minHeight: '100vh', background: 'var(--surface)', display: 'flex', flexDirection: 'column' }}>
      <header className="app-header">
        <div className="app-header__left">
          {/* Hamburger Menu Toggle (Visible on Mobile & Tablet) */}
          <button
            type="button"
            className="app-header__menu-btn"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation menu"
            aria-expanded={drawerOpen}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>

          <Link to="/dashboard" className="app-header__logo-link">
            {topNavLogo ? (
              <img
                src={topNavLogo}
                alt={orgName || 'Organization Logo'}
                className="app-header__tenant-logo"
                onError={(e) => {
                  (e.target as HTMLElement).style.display = 'none';
                }}
              />
            ) : (
              <span className="app-header__brand-title">
                Trainova<span style={{ color: 'var(--blue)' }}>.ai</span>
              </span>
            )}
            {orgName && !topNavLogo && (
              <span className="app-header__org-pill" title={orgName}>
                {orgName}
              </span>
            )}
          </Link>

          {/* Desktop Navigation Links */}
          <nav className="app-header__desktop-nav" aria-label="Main Navigation">
            <Link
              to="/dashboard"
              aria-label="Learner Portal"
              title="Learner Portal"
              className={`app-header__nav-link ${isDashboardSection ? 'app-header__nav-link--active' : ''}`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect width="7" height="9" x="3" y="3" rx="1" />
                <rect width="7" height="5" x="14" y="3" rx="1" />
                <rect width="7" height="9" x="14" y="12" rx="1" />
                <rect width="7" height="5" x="3" y="16" rx="1" />
              </svg>
              <span>{t('nav.dashboard', undefined, 'Learner Portal')}</span>
            </Link>

            <Link
              to="/attempts"
              aria-label={t('nav.my_attempts', undefined, 'My Attempts')}
              title={t('nav.my_attempts', undefined, 'My Attempts')}
              className={`app-header__nav-link ${isAttemptsSection ? 'app-header__nav-link--active' : ''}`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20v-6M6 20V10M18 20V4" />
              </svg>
              <span>{t('nav.my_attempts', undefined, 'My Attempts')}</span>
            </Link>

            {can(Permissions.SCENARIO_READ) && (
              <Link
                to="/scenarios"
                aria-label={t('nav.scenarios', undefined, 'Scenarios Studio')}
                title={t('nav.scenarios', undefined, 'Scenarios Studio')}
                className={`app-header__nav-link ${isScenarioSection ? 'app-header__nav-link--active' : ''}`}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m16 6 4 14M12 6v14M8 8v12M4 4v16" />
                </svg>
                <span>{t('nav.scenarios', undefined, 'Scenarios Studio')}</span>
              </Link>
            )}

            {(isTenantAdmin || isPlatformAdmin || can(Permissions.ANALYTICS_EXPORT)) && (
              <Link
                to="/admin/reports"
                aria-label={t('nav.reports', undefined, 'Reports')}
                title={t('nav.reports', undefined, 'Reports')}
                className={`app-header__nav-link ${isReportsSection ? 'app-header__nav-link--active' : ''}`}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
                <span>{t('nav.reports', undefined, 'Reports')}</span>
              </Link>
            )}

            {(isTenantAdmin || isPlatformAdmin) && (
              <Link
                to="/admin"
                aria-label={t('nav.admin_console', undefined, 'Admin Console')}
                title={t('nav.admin_console', undefined, 'Admin Console')}
                className={`app-header__nav-link ${isAdminSection ? 'app-header__nav-link--active' : ''}`}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                <span>{t('nav.admin_console', undefined, 'Admin Console')}</span>
              </Link>
            )}
          </nav>
        </div>

        {/* Right side user menu & language selector */}
        <div className="app-header__right">
          <LanguageSelector variant="header" className="app-header__lang-selector" />

          <div className="app-header__desktop-user">
            <div
              className="pav"
              title={user?.email || 'Signed-in user'}
              style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'var(--blue)', color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: '12px', flexShrink: 0 }}
            >
              {user?.email ? user.email.substring(0, 2).toUpperCase() : 'U'}
            </div>
            <span className="app-header__user-email" title={user?.email}>
              {user?.email} <span className="role">{user?.role}</span>
            </span>
            <button
              className="app-header__logout-btn btn ghost"
              onClick={logout}
              type="button"
            >
              {t('nav.sign_out', undefined, 'Sign out')}
            </button>
          </div>

          {/* Compact Mobile Avatar that opens the Navigation Drawer */}
          <div
            className="app-header__mobile-avatar"
            onClick={() => setDrawerOpen(true)}
            title={user?.email || 'User menu'}
            role="button"
            tabIndex={0}
            aria-label="Open navigation drawer"
          >
            {user?.email ? user.email.substring(0, 2).toUpperCase() : 'U'}
          </div>
        </div>
      </header>

      {/* Slide-out Mobile Navigation Drawer */}
      <ResponsiveNavDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        branding={branding}
      />

      <main className="app-main">
        <Outlet />
      </main>

      {/* Fixed Mobile Bottom Tab Bar — hidden on desktop via CSS */}
      <MobileBottomNav onOpenDrawer={() => setDrawerOpen(true)} />
    </div>
  );
}

